import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Ban, Clock, KeyRound, Link2, ShieldCheck } from "lucide-react";
import { ApiError } from "@/api/client";
import { utenzeApi } from "@/api/utenze";
import { useAuth } from "@/auth/useAuth";
import { RiaccediDialog } from "@/components/RiaccediDialog";
import type { Fornitore, UtenzaFornitoreInfo } from "@/types";

// Il backend rifiuta con un codice: qui diventa una frase che dice la causa e la
// via d'uscita (regole ux "error-clarity" / "error-recovery").
const MOTIVO_RIFIUTO: Record<string, string> = {
  fuori_finestra_fornitore: "A2A risponde dalle 9 alle 23: riprova in quella fascia",
  prova_in_corso: "Una verifica è già in corso su questa utenza",
  utenza_non_assegnata: "Nessuna utenza A2A assegnata: chiedi all'amministratore",
  utenza_predefinita: "Questa utenza è la predefinita del portale: la gestisce l'amministratore.",
  fornitore_non_disponibile: "Il collegamento a questo fornitore non è ancora disponibile.",
  totp_secret_obbligatorio: "Serve la chiave dell'app di autenticazione.",
  codice_obbligatorio: "Serve il codice dell'app autenticatore.",
  nessuna_utenza_collegata: "Nessuna utenza collegata a questo fornitore.",
};

function messaggioErrore(e: unknown): string {
  const codice = e instanceof ApiError ? e.detail : "";
  return MOTIVO_RIFIUTO[codice] ?? "Operazione non riuscita. Riprova tra poco.";
}

// Come lavora l'utenza, in sola lettura: la modalità la decide l'amministratore
// quando crea l'utenza, dal profilo non si cambia.
const RIGA_MODALITA: Record<string, string> = {
  custodite: "Il portale conserva le credenziali",
  sessione_temporanea: "Sessione temporanea: accedi con il codice della tua app",
};

type Tono = "neutro" | "ambra" | "rosso";

const CLASSE_TONO: Record<Tono, string> = {
  neutro: "text-muted-foreground",
  ambra: "text-amber-700",
  rosso: "text-red-700",
};

// Lo stato non si legge dal solo colore: sempre icona + parole (regola a11y
// "color-not-only"), come nella riga di stato in testata. Gli stati sono
// esclusivi e nell'ordine della spec: predefinita, non assegnata, credenziali
// da inserire, collegata, accesso fermo.
function statoUtenza(u: UtenzaFornitoreInfo): { testo: string; tono: Tono; Icona: typeof Link2 } {
  if (!u.disponibile) {
    return { testo: "Non ancora disponibile per questo fornitore", tono: "neutro", Icona: Ban };
  }
  if (u.predefinita) {
    return {
      testo: "Utenza assegnata da AIEnergia: le pratiche vengono intestate ad AIEnergia",
      tono: "neutro",
      Icona: ShieldCheck,
    };
  }
  if (!u.assegnata) {
    return {
      testo:
        "Nessuna utenza A2A assegnata: le pratiche restano in coda finché l'amministratore non la assegna",
      tono: "neutro",
      Icona: Ban,
    };
  }
  if (u.stato === "da_ripristinare") {
    return {
      testo: u.motivo_stato ? `Accesso da ripristinare: ${u.motivo_stato}` : "Accesso da ripristinare",
      tono: "rosso",
      Icona: AlertTriangle,
    };
  }
  if (u.stato === "sessione_scaduta") {
    return { testo: "Sessione scaduta", tono: "ambra", Icona: Clock };
  }
  if (u.stato === "da_configurare") {
    return { testo: "Credenziali da inserire", tono: "neutro", Icona: KeyRound };
  }
  return { testo: "Collegata", tono: "neutro", Icona: Link2 };
}

/**
 * Sezione «Utenze fornitore» del profilo agenzia: una card per fornitore con lo
 * stato in chiaro e, per l'Affiliato, le credenziali dell'utenza che l'admin ha
 * assegnato all'agenzia. Quale utenza sia, e come lavori, non si sceglie da qui.
 * L'Operatore la legge e basta (regola ux "read-only-distinction": niente
 * bottoni disabilitati, proprio nessun bottone).
 */
export function UtenzeFornitoreSezione() {
  const { hasRole } = useAuth();
  const affiliato = hasRole("affiliato");

  const [utenze, setUtenze] = useState<UtenzaFornitoreInfo[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [erroreLista, setErroreLista] = useState<string | null>(null);

  const [formAperto, setFormAperto] = useState<Fornitore | null>(null);
  const [esito, setEsito] = useState<{ fornitore: Fornitore; testo: string } | null>(null);
  const [daRiaccedere, setDaRiaccedere] = useState<UtenzaFornitoreInfo | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErroreLista(null);
    try {
      setUtenze(await utenzeApi.lista());
    } catch {
      setErroreLista("Utenze fornitore non disponibili al momento.");
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  return (
    <section data-tour="profilo-utenze" className="bg-card p-6 rounded-lg border border-border">
      <h2 className="text-lg font-semibold text-foreground mb-1">Utenze fornitore</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Con quale utenza il portale invia le pratiche a ogni fornitore.
      </p>

      {caricamento ? (
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      ) : erroreLista ? (
        <div className="flex flex-wrap items-center gap-3">
          <p role="alert" className="text-sm text-red-600">
            {erroreLista}
          </p>
          <button
            type="button"
            onClick={() => void carica()}
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Riprova
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {utenze.map((u) => {
            const { testo, tono, Icona } = statoUtenza(u);
            // Le credenziali si mettono solo sull'utenza che l'admin ha assegnato
            // all'agenzia: sulla predefinita il backend risponde 403.
            const propria = u.disponibile && u.assegnata && !u.predefinita;
            const primoInserimento = u.stato === "da_configurare";
            const riaccedi = u.stato === "sessione_scaduta" && u.modalita === "sessione_temporanea";
            return (
              <div key={u.fornitore} className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <h3 className="font-medium text-foreground">{u.fornitore}</h3>
                    <p className={`text-sm flex items-start gap-1.5 ${CLASSE_TONO[tono]}`}>
                      <Icona size={14} aria-hidden="true" className="shrink-0 mt-0.5" />
                      <span>{testo}</span>
                    </p>
                    {propria && u.username && (
                      <p className="text-xs text-muted-foreground break-all">
                        Utenza: <span className="font-medium text-foreground">{u.username}</span>
                      </p>
                    )}
                    {propria && u.modalita && (
                      <p className="text-xs text-muted-foreground">{RIGA_MODALITA[u.modalita]}</p>
                    )}
                    {propria && u.condivisa_con > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Condivisa con altre {u.condivisa_con} agenzie
                      </p>
                    )}
                  </div>

                  {affiliato && propria && (
                    <div data-tour="profilo-credenziali" className="flex flex-wrap gap-2 shrink-0">
                      {riaccedi ? (
                        <button
                          type="button"
                          onClick={() => setDaRiaccedere(u)}
                          className="px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        >
                          Riaccedi
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEsito(null);
                            setFormAperto(u.fornitore);
                          }}
                          className="px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        >
                          {primoInserimento ? "Inserisci credenziali" : "Aggiorna credenziali"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {esito?.fornitore === u.fornitore && formAperto !== u.fornitore && (
                  <p
                    aria-live="polite"
                    className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                  >
                    {esito.testo}
                  </p>
                )}

                {formAperto === u.fornitore && (
                  <FormCredenziali
                    fornitore={u.fornitore}
                    username={u.username ?? ""}
                    modalita={u.modalita ?? "custodite"}
                    onAnnulla={() => setFormAperto(null)}
                    onSalvata={async () => {
                      setFormAperto(null);
                      setEsito({ fornitore: u.fornitore, testo: "Credenziali salvate." });
                      await carica();
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {daRiaccedere && (
        <RiaccediDialog
          fornitore={daRiaccedere.fornitore}
          username={daRiaccedere.username ?? ""}
          open
          onClose={() => setDaRiaccedere(null)}
          onSuccess={() => {
            setDaRiaccedere(null);
            void carica();
          }}
        />
      )}
    </section>
  );
}

interface FormProps {
  fornitore: Fornitore;
  username: string;
  modalita: string;
  onAnnulla: () => void;
  onSalvata: () => void | Promise<void>;
}

/**
 * Form in linea (non modale) per le credenziali dell'utenza assegnata: compare
 * solo quando serve (regola ux "progressive-disclosure"). Il nome utente si
 * legge e basta (regola ux "read-only-distinction"), la modalità decide se
 * serve la chiave dell'app o il codice del momento.
 */
function FormCredenziali({ fornitore, username, modalita, onAnnulla, onSalvata }: FormProps) {
  const [password, setPassword] = useState("");
  const [chiave, setChiave] = useState("");
  const [codice, setCodice] = useState("");
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const custodite = modalita === "custodite";
  const pronto = password.length > 0 && (custodite ? chiave.trim().length > 0 : codice.length === 6);

  const id = (campo: string) => `utenza-${fornitore}-${campo}`;

  async function salva() {
    setErrore(null);
    setAttesa(true);
    try {
      const { job_id } = await utenzeApi.collega(fornitore, {
        password,
        ...(custodite ? { totp_secret: chiave.trim() } : { codice }),
      });
      const prova = await utenzeApi.attendiProva(fornitore, job_id);
      if (prova.stato === "ok") {
        await onSalvata();
        return;
      }
      setErrore(prova.dettaglio);
      setCodice("");
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setAttesa(false);
    }
  }

  return (
    <div className="border-t border-border pt-3 space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="text-sm font-medium text-foreground">Nome utente</span>
          <p className="h-9 flex items-center text-sm text-foreground font-medium break-all">
            {username}
          </p>
        </div>
        <div className="space-y-1">
          <label htmlFor={id("password")} className="text-sm font-medium text-foreground">
            Password
          </label>
          <input
            id={id("password")}
            type="password"
            autoComplete="new-password"
            value={password}
            disabled={attesa}
            onChange={(e) => setPassword(e.target.value)}
            className="h-9 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 text-sm focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          />
        </div>
      </div>

      {custodite ? (
        <div className="space-y-1">
          <label htmlFor={id("chiave")} className="text-sm font-medium text-foreground">
            Chiave dell'app di autenticazione
          </label>
          <textarea
            id={id("chiave")}
            rows={2}
            value={chiave}
            disabled={attesa}
            onChange={(e) => setChiave(e.target.value)}
            aria-describedby={id("chiave-help")}
            className="w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm font-mono break-all focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          />
          <p id={id("chiave-help")} className="text-xs text-muted-foreground">
            È il codice che A2A mostra accanto al QR quando registri l'app. Va bene anche l'indirizzo che
            inizia con otpauth://
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <label htmlFor={id("codice")} className="text-sm font-medium text-foreground">
            Codice dell'app autenticatore
          </label>
          <input
            id={id("codice")}
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            value={codice}
            disabled={attesa}
            onChange={(e) => setCodice(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-9 w-32 rounded-lg border border-input bg-transparent px-2.5 text-sm text-center tracking-widest font-mono focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          />
        </div>
      )}

      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
        <AlertTriangle size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
        <span>
          Salvando, il portale accede ad A2A per verificare le credenziali: chi in agenzia è dentro
          A2A dal browser con questa utenza verrà disconnesso.
        </span>
      </p>

      {errore && (
        <p role="alert" className="text-sm text-red-600">
          {errore}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onAnnulla}
          disabled={attesa}
          className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={() => void salva()}
          disabled={!pronto || attesa}
          className="px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {attesa && (
            <span
              aria-hidden="true"
              className="h-3.5 w-3.5 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin"
            />
          )}
          {attesa ? "Verifica in corso…" : "Salva e verifica"}
        </button>
      </div>
    </div>
  );
}
